/**
 * #10426-a1 (MST-6431) — sticky shared fallback per output device + Connector version.
 *
 * Once exclusive_play times out on a device (and the exclusive engine is not playing), every later
 * PCM play on that device goes straight to shared: no exclusive_play attempt, no pre-exclusive
 * shared stop, no exclusive timer. Next / skip / auto-advance start immediately.
 * Cleared when the Connector version changes (a fixed build gets a fresh bit-perfect attempt) or
 * when the user turns Exclusive Mode back on for that device. A zone change does NOT clear it.
 * DSD is never routed shared (HTML5 cannot decode it) — DSD keeps its exclusive path.
 * Stored in memory + localStorage; every storage access is wrapped (private mode / blocked storage).
 */
import { create } from 'zustand';
import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from './diagnosticLogger';
import { getConnectorVersion } from './connectorVersion';
import { isDsd } from './formatDetection';

export const STICKY_SHARED_STORAGE_KEY = 'maestro_sticky_shared_fallback_v1';

export interface StickySharedMark {
  /** All identifiers seen for this device (audio_device_id, DAC id/uid/name) — lower-cased. */
  keys: string[];
  connectorVersion: string;
  reason: string;
  at: number;
  /** Sessions in which exclusive_play timed out on this device + Connector version. */
  timeouts: number;
}

/** A first timeout is sticky for this session only; the second (same device + version) persists. */
export const STICKY_PERSIST_AFTER_TIMEOUTS = 2;

type ZoneLike = { id?: string; name?: string; type?: string; metadata?: { audio_device_id?: string } } | null | undefined;
type Marks = Record<string, StickySharedMark>;

function norm(v: unknown): string {
  return String(v ?? '').trim().toLowerCase();
}

/** Stored records: timeout counters for every device; only those at the persist threshold route sticky on load. */
function readRecords(): Marks {
  try {
    const raw = localStorage.getItem(STICKY_SHARED_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeRecords(records: Marks): void {
  try {
    if (Object.keys(records).length === 0) localStorage.removeItem(STICKY_SHARED_STORAGE_KEY);
    else localStorage.setItem(STICKY_SHARED_STORAGE_KEY, JSON.stringify(records));
  } catch {
    /* storage blocked — memory copy still applies this session */
  }
}

function persistedMarks(records: Marks): Marks {
  return Object.fromEntries(Object.entries(records).filter(([, m]) => (m.timeouts ?? 0) >= STICKY_PERSIST_AFTER_TIMEOUTS));
}

export const useStickySharedStore = create<{ marks: Marks }>(() => ({ marks: persistedMarks(readRecords()) }));

function dropKeys(keys: string[]): void {
  const drop = (m: Marks) => Object.fromEntries(Object.entries(m).filter(([k]) => !keys.includes(k)));
  useStickySharedStore.setState({ marks: drop(useStickySharedStore.getState().marks) });
  writeRecords(drop(readRecords()));
}

/** '' = version not known yet (Connector info not loaded) — unknown never invalidates a mark. */
function versionMismatch(markVersion: string, current: string): boolean {
  return current !== '' && markVersion !== current;
}

/** Primary device key for a local-native zone: metadata.audio_device_id, else zone name / id. */
export function zoneDeviceKey(zone: ZoneLike): string | null {
  if (!zone || zone.type !== 'local-native') return null;
  return norm(zone.metadata?.audio_device_id) || norm(zone.name) || norm(zone.id) || null;
}

function findMark(marks: Record<string, StickySharedMark>, zone: ZoneLike): [string, StickySharedMark] | null {
  const key = zoneDeviceKey(zone);
  if (!key) return null;
  if (marks[key]) return [key, marks[key]];
  const hit = Object.entries(marks).find(([, m]) => m.keys.includes(key));
  return hit || null;
}

/** Pure read (safe in render): the mark for this zone when it matches the running Connector version. */
export function stickySharedMarkFor(
  marks: Record<string, StickySharedMark>,
  zone: ZoneLike,
): StickySharedMark | null {
  const hit = findMark(marks, zone);
  if (!hit) return null;
  return versionMismatch(hit[1].connectorVersion, getConnectorVersion()) ? null : hit[1];
}

/** Active mark for the zone; drops a mark left by a different Connector version. */
export function getStickySharedMark(zone: ZoneLike): StickySharedMark | null {
  const marks = useStickySharedStore.getState().marks;
  const hit = findMark(marks, zone);
  if (!hit) return null;
  const [key, mark] = hit;
  const current = getConnectorVersion();
  if (versionMismatch(mark.connectorVersion, current)) {
    dropKeys([key]);
    diagnosticLog('info', 'playback', 'sticky shared cleared — connector version changed', {
      device: key, markedOn: mark.connectorVersion, connector: current,
    });
    return null;
  }
  return mark;
}

export function isStickySharedActiveForZone(zone: ZoneLike): boolean {
  return getStickySharedMark(zone) !== null;
}

let lastRoutedTrackKey: string | null = null;

/** Routing gate: true when this play must skip exclusive and go shared (never for DSD). */
export function shouldRouteStickyShared(zone: ZoneLike, track: unknown): boolean {
  if (!isStickySharedActiveForZone(zone)) return false;
  if (track && isDsd(track)) return false;
  const t = track as { id?: unknown } | null | undefined;
  const routeKey = `${zoneDeviceKey(zone)}|${String(t?.id ?? '')}`;
  if (routeKey !== lastRoutedTrackKey) {
    lastRoutedTrackKey = routeKey;
    diagnosticLog('info', 'playback', 'sticky shared — exclusive skipped', { device: zoneDeviceKey(zone), trackId: t?.id ?? null });
  }
  return true;
}

/**
 * Remember that exclusive timed out on this device for this Connector version.
 * First timeout: sticky for this session (memory). Second session with a timeout: persisted.
 * A DSD timeout never marks — DSD has no shared path, and a slow DSD open says nothing about PCM.
 */
export function markStickySharedFallback(zone: ZoneLike, reason: string, track?: unknown): void {
  const key = zoneDeviceKey(zone);
  if (!key) return;
  if (track && isDsd(track)) {
    diagnosticLog('info', 'playback', 'sticky shared not set — DSD timeout', { device: key });
    return;
  }
  const version = getConnectorVersion();
  const active = useStickySharedStore.getState().marks[key];
  if (active && !versionMismatch(active.connectorVersion, version)) return; // already counted this session
  const dac = useMaestroStore.getState().selectedUsbDac as { id?: string; uid?: string; name?: string } | null | undefined;
  const keys = Array.from(new Set([key, norm(zone?.name), norm(zone?.id), norm(dac?.id), norm(dac?.uid), norm(dac?.name)].filter(Boolean)));
  const records = readRecords();
  const prior = records[key] && records[key].connectorVersion === version ? records[key].timeouts ?? 0 : 0;
  const mark: StickySharedMark = { keys, connectorVersion: version, reason, at: Date.now(), timeouts: prior + 1 };
  writeRecords({ ...records, [key]: mark });
  useStickySharedStore.setState({ marks: { ...useStickySharedStore.getState().marks, [key]: mark } });
  diagnosticLog('warn', 'playback', 'sticky shared set — later plays on this device skip exclusive', {
    device: key, connector: version, reason, timeouts: mark.timeouts, persisted: mark.timeouts >= STICKY_PERSIST_AFTER_TIMEOUTS,
  });
}

/** Exclusive Mode toggled for a device (Devices page, either direction) — give bit-perfect a fresh attempt. */
export function clearStickySharedForDevice(ids: Array<string | null | undefined>, cause: string): void {
  const wanted = new Set(ids.map(norm).filter(Boolean));
  if (wanted.size === 0) return;
  const all = { ...readRecords(), ...useStickySharedStore.getState().marks };
  const hit = Object.entries(all).filter(([k, m]) => wanted.has(k) || m.keys.some((x) => wanted.has(x))).map(([k]) => k);
  if (hit.length === 0) return;
  dropKeys(hit);
  diagnosticLog('info', 'playback', 'sticky shared cleared', { cause, ids: Array.from(wanted) });
}

/** Test-only: simulate an app restart — memory reloads from storage (persisted marks only). */
export function __reloadStickySharedFromStorageForTests(): void {
  lastRoutedTrackKey = null;
  useStickySharedStore.setState({ marks: persistedMarks(readRecords()) });
}

/** Test-only reset (memory + storage). */
export function __resetStickySharedForTests(): void {
  lastRoutedTrackKey = null;
  useStickySharedStore.setState({ marks: {} });
  writeRecords({});
}
